import { MouseEventHandler, MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import { PlusCircleOutlined } from "@ant-design/icons";
import { CodeNode } from "@lexical/code";
import { LinkNode, TOGGLE_LINK_COMMAND, $createLinkNode, $isLinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { Col, Divider, Form, type FormInstance, Input, Popover, Radio, Tooltip } from "antd";
import { NamePath } from "antd/lib/form/interface";
import { clsx } from "clsx";
import Picker, { IEmojiData } from "emoji-picker-react";
import {
    $createTextNode,
    $getSelection,
    $isTextNode,
    $setSelection,
    CLEAR_EDITOR_COMMAND,
    FORMAT_TEXT_COMMAND,
    LexicalEditor,
} from "lexical";

import {
    RVForm,
    RVFormItem,
    RVIcon,
    RVIconSizeOptions,
    useClickOutside,
    RVLexicalEditor,
    validateRequiredLexicalField,
    INSERT_TEXT,
    useLexicalToolbar,
    ConfigurableVariablesPickerPlugin,
    CustomVariablesHighlightPlugin,
    ESCAPE_BARS_TRIGGERS,
    ESCAPE_HIGHLIGHT_LIST,
} from "@regal-voice/ui";
import ExpandableField from "Components/elements/ExpandableField/ExpandableField";
import { SectionTitle } from "Components/shared/SectionTypography/SectionTypography";
import { useContactVariableOptions } from "Hooks/useContactVariableOptions";
import { EmailLinkPopoverManager } from "Pages/agentDesktop/Pages/AgentDesktopTasks/Components/AgentTaskInterface/ActivityView/EmailInput/EmailLinkPopoverManager/EmailLinkPopoverManager";
import {
    LinkEdit,
    LinkEditProps,
} from "Pages/agentDesktop/Pages/AgentDesktopTasks/Components/AgentTaskInterface/ActivityView/EmailInput/LinkEdit/LinkEdit";
import sharedStyles from "Pages/agentDesktop/Pages/AgentDesktopTasks/Components/AgentTaskInterface/ActivityView/SharedInputStyling.module.scss";
import { TeamsInputs } from "Pages/settings/Pages/Shared/TeamsInputs/TeamsInputs";
import { CommunicationTemplateDTO } from "Types/CommunicationTemplate";

import styles from "./CommunicationTemplateForm.module.scss";

const TOOLTIP_DISPLAY_DELAY = 1;

export interface CommunicationTemplateFormProps {
    form: FormInstance;
    initialFormValues: Partial<CommunicationTemplateDTO>;
    onFinish: (formData: CommunicationTemplateDTO) => void;
    editorRef: MutableRefObject<LexicalEditor | null | undefined>;
}

export default function CommunicationTemplateForm({
    form,
    initialFormValues,
    onFinish,
    editorRef,
}: CommunicationTemplateFormProps) {
    const [activeEditor, setActiveEditor] = useState<LexicalEditor | null>(null);

    const popoverRef = useRef<any>(null);
    const iconRef = useRef<any>(null);

    const [emojiPickerOpen, setEmojiPickerOpen] = useState<boolean>(false);
    const [isEditLinkOpen, setIsEditLinkOpen] = useState(false);
    const [currentSelection, setCurrentSelection] = useState<Range | null>(null);
    const { options: contactVariableOptions, loading: contactVariablesLoading } = useContactVariableOptions();

    const channel = Form.useWatch("channel", form) ?? initialFormValues.channel;
    const channelName = channel ? (channel === "email" ? "Email" : "SMS") : "Template";

    const shouldShowRichTextToolbar = channel === "email";

    const [oldChannel, setOldChannel] = useState(channel);

    const { isBold, isItalic, isUnderline } = useLexicalToolbar(activeEditor);

    const insertEmoji = useCallback(
        (event: React.MouseEvent<Element, MouseEvent>, data: IEmojiData) => {
            setEmojiPickerOpen(false);
            activeEditor?.dispatchCommand(INSERT_TEXT, data.emoji);
        },
        [activeEditor]
    );

    const handleEmojiPickerOpen = useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            e.stopPropagation();
            setEmojiPickerOpen(!emojiPickerOpen);
        },
        [emojiPickerOpen]
    );

    const handleCreateLink: LinkEditProps["onConfirm"] = useCallback(
        ({ link, displayText }) => {
            setIsEditLinkOpen(false);

            // TODO move all this section into a plugin
            const editor = activeEditor;
            editor?.update(() => {
                const selection = $getSelection();
                if (selection?.isCollapsed()) {
                    selection?.insertNodes([$createLinkNode(link).append($createTextNode(displayText))]);
                } else {
                    activeEditor?.dispatchCommand(TOGGLE_LINK_COMMAND, link);
                }

                $setSelection(null);
            });
        },
        [activeEditor]
    );

    const handleLinkIconClick: MouseEventHandler = useCallback((event) => {
        const selection = window.getSelection();
        if (selection) {
            setCurrentSelection(selection.getRangeAt(0));
            setIsEditLinkOpen(true);
        }
        // we need to make sure that this click doesn't fire the LinkEdit's onClickOutside
        event.stopPropagation();
    }, []);

    const handleLexicalEditLink: LinkEditProps["onConfirm"] = useCallback(
        ({ link, displayText }) => {
            const editor = activeEditor;
            if (!editor) {
                return;
            }

            editor.update(() => {
                const selection = $getSelection();

                // for some reason, Lexical always creates a `<span />` inside every anchor tags // TODO look into this
                // in the meantime, we're going to grab the text node, then look at the parent node
                // which should be a link node
                const [textNode] = selection?.getNodes() ?? [];
                if (!$isTextNode(textNode)) {
                    // nothing to do, the node structure doesn't match our expected hierarchy
                    return;
                }

                const anchorNode = textNode?.getParent();
                if (!$isLinkNode(anchorNode)) {
                    // nothing to do, the node structure doesn't match our expected hierarchy
                    return;
                }

                // we now have references to both the anchor node, to update the url
                // and the text node, where we'll update the text contents
                anchorNode.setURL(link);
                textNode.setTextContent(displayText);

                $setSelection(null);
            });
        },
        [activeEditor]
    );

    const handleLexicalRemoveLink = useCallback(() => {
        activeEditor?.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }, [activeEditor]);

    const handleCloseLinkPopover = useCallback(() => {
        setIsEditLinkOpen(false);
    }, []);

    useEffect(() => {
        // when we switch between "sms" and "email" channels
        // we want to update the parent component's reference to the active editor
        editorRef.current = activeEditor;
    }, [activeEditor, editorRef]);

    useEffect(() => {
        if (channel !== oldChannel) {
            setTimeout(() => {
                // the `activeEditor` instance will only be updated on the next tick
                activeEditor?.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);

                // when we clear the editor, the focus will be moved to the editor
                // setting the selection to `null` will prevent the editor to steal the focus
                activeEditor?.update(() => {
                    $setSelection(null);
                });
                // when switching the channel we want to clear the contents for the "body" field
                setOldChannel(channel);
            });
        }
    }, [channel, oldChannel, activeEditor]);

    const triggerValidation = useCallback(
        (fields: NamePath) => {
            form.validateFields(fields);
        },
        [form]
    );

    useClickOutside([popoverRef, iconRef], () => setEmojiPickerOpen(false));

    return (
        <RVForm layout="vertical" size="large" form={form} initialValues={initialFormValues} onFinish={onFinish}>
            {initialFormValues.id && (
                <RVFormItem hidden name="id">
                    <Input type="hidden" data-testid="template-id" />
                </RVFormItem>
            )}

            <RVFormItem hidden name="state">
                <Input type="hidden" data-testid="state" />
            </RVFormItem>

            <Col span="12">
                <SectionTitle>Template Details</SectionTitle>

                <RVFormItem
                    name="name"
                    label="Template Name"
                    required
                    isFullPageForm
                    rules={[
                        {
                            required: true,
                            message: "Template Name is required",
                        },
                    ]}
                >
                    <Input autoComplete="off" data-testid="name" autoFocus={true} />
                </RVFormItem>

                <ExpandableField
                    collapsedText="Add Description"
                    expandedText="Remove Description"
                    isExpanded={Boolean(initialFormValues.description?.length)}
                    className="large-input-field"
                    icon={<PlusCircleOutlined className={styles.addDescriptionIcon} />}
                >
                    <RVFormItem
                        label="Description (Optional)"
                        name="description"
                        description="Visible to internal users only."
                        isFullPageForm
                    >
                        <Input
                            placeholder="Template Description (Optional)"
                            autoComplete="off"
                            data-testid="description"
                        />
                    </RVFormItem>
                </ExpandableField>

                <RVFormItem
                    label="Template Channel"
                    required={true}
                    name="channel"
                    description="Whether the disposition indicates that a conversation happened on the task."
                    rules={[{ required: true, message: "Template Channel is a required field." }]}
                    isFullPageForm
                >
                    <Radio.Group className="selection-radio" size="small">
                        <Radio className={styles.radio} value="sms" data-testid="channel-sms">
                            SMS
                        </Radio>
                        <Radio className={styles.radio} value="email" data-testid="channel-email">
                            Email
                        </Radio>
                    </Radio.Group>
                </RVFormItem>

                <TeamsInputs
                    title="Template"
                    showTeams={Boolean(initialFormValues.teamIds?.length)}
                    dropdownSize="large"
                />
            </Col>

            <Divider />

            <Col span="12">
                <SectionTitle>Template Design</SectionTitle>

                {channel === "email" && (
                    <RVFormItem
                        label="Email Subject Line"
                        name="emailSubject"
                        description="Email replies will automatically inherit the subject line of the existing thread."
                        isFullPageForm
                    >
                        <Input data-testid="email-subject" />
                    </RVFormItem>
                )}

                <RVFormItem
                    label={`${channelName} Body`}
                    required={true}
                    description="Compose the message you'd like to save to this Template."
                    isFullPageForm
                >
                    <div className={styles.textAreaWrapper}>
                        <EmailLinkPopoverManager
                            expanded={false}
                            onLinkEdit={handleLexicalEditLink}
                            onLinkRemove={handleLexicalRemoveLink}
                            className={styles.popoverManager}
                        >
                            <RVFormItem
                                name="bodySource"
                                noStyle
                                rules={[validateRequiredLexicalField("Body content for this template is required.")]}
                                validateTrigger={["onSubmit"]}
                            >
                                <RVLexicalEditor
                                    key={channel}
                                    editorRef={setActiveEditor}
                                    className={styles.templateBody}
                                    data-testid="body"
                                    isPlainText={channel === "sms"}
                                    // TODO build a better API to overwrite nodes
                                    nodeOverrides={[
                                        ListNode,
                                        ListItemNode,
                                        HorizontalRuleNode,
                                        HeadingNode,
                                        QuoteNode,
                                        CodeNode,
                                        LinkNode,
                                    ]}
                                    onKeyUp={() => triggerValidation(["bodySource"])}
                                >
                                    <CustomVariablesHighlightPlugin prioritiziedRegexList={ESCAPE_HIGHLIGHT_LIST} />
                                    <ConfigurableVariablesPickerPlugin
                                        prioritizedTriggers={ESCAPE_BARS_TRIGGERS}
                                        forceWrap={ESCAPE_BARS_TRIGGERS[0]}
                                        options={contactVariableOptions}
                                        loading={contactVariablesLoading}
                                    />
                                    <LinkPlugin />
                                    <ClearEditorPlugin />
                                </RVLexicalEditor>
                            </RVFormItem>
                        </EmailLinkPopoverManager>

                        {/* Toolbar */}
                        <div className={clsx(sharedStyles.icons, styles.iconsContainer)}>
                            <div className={clsx(sharedStyles.leftGroup, styles.toolbar)}>
                                <Popover
                                    content={
                                        <div ref={popoverRef}>
                                            <Picker onEmojiClick={insertEmoji} disableAutoFocus={true} native />
                                        </div>
                                    }
                                    open={emojiPickerOpen}
                                    overlayClassName={styles.popover}
                                >
                                    <Tooltip title="Emojis" mouseEnterDelay={TOOLTIP_DISPLAY_DELAY}>
                                        <span
                                            ref={iconRef}
                                            onClick={handleEmojiPickerOpen}
                                            className={styles.refWrapper}
                                        >
                                            <RVIcon
                                                icon="smiley"
                                                color={"gray-4"}
                                                className={styles.actionIcon}
                                                size={RVIconSizeOptions.LARGE}
                                            />
                                        </span>
                                    </Tooltip>
                                </Popover>

                                {shouldShowRichTextToolbar && (
                                    <>
                                        <Divider type="vertical" className={sharedStyles.divider} />
                                        {/* Bold */}
                                        <Tooltip title="Bold" mouseEnterDelay={TOOLTIP_DISPLAY_DELAY}>
                                            <RVIcon
                                                icon="bold"
                                                color={"gray-5"}
                                                className={clsx(sharedStyles.actionIcon, {
                                                    [styles.activeButton]: isBold,
                                                })}
                                                size={RVIconSizeOptions.LARGE}
                                                data-dd-action-name="Toggle text format bold"
                                                onClick={() =>
                                                    activeEditor?.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")
                                                }
                                            />
                                        </Tooltip>
                                        {/* Italic */}
                                        <Tooltip title="Italic" mouseEnterDelay={TOOLTIP_DISPLAY_DELAY}>
                                            <RVIcon
                                                icon="italic"
                                                color={"gray-5"}
                                                className={clsx(sharedStyles.actionIcon, {
                                                    [styles.activeButton]: isItalic,
                                                })}
                                                size={RVIconSizeOptions.LARGE}
                                                data-dd-action-name="Toggle text format italic"
                                                onClick={() =>
                                                    activeEditor?.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")
                                                }
                                            />
                                        </Tooltip>
                                        {/* Underline */}
                                        <Tooltip title="Underline" mouseEnterDelay={TOOLTIP_DISPLAY_DELAY}>
                                            <RVIcon
                                                icon="underline"
                                                color={"gray-5"}
                                                className={clsx(sharedStyles.actionIcon, {
                                                    [styles.activeButton]: isUnderline,
                                                })}
                                                size={RVIconSizeOptions.LARGE}
                                                data-dd-action-name="Toggle text format underline"
                                                onClick={() =>
                                                    activeEditor?.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")
                                                }
                                            />
                                        </Tooltip>
                                        <Divider type="vertical" className={sharedStyles.divider} />

                                        <Popover
                                            content={
                                                <LinkEdit
                                                    link=""
                                                    displayText={currentSelection?.toString() ?? ""}
                                                    isNew
                                                    onClickOutside={handleCloseLinkPopover}
                                                    onConfirm={handleCreateLink}
                                                />
                                            }
                                            open={isEditLinkOpen}
                                            destroyTooltipOnHide
                                        >
                                            <RVIcon
                                                icon="link"
                                                color={"gray-5"}
                                                className={sharedStyles.actionIcon}
                                                size={RVIconSizeOptions.LARGE}
                                                onMouseDown={handleLinkIconClick}
                                            />
                                        </Popover>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </RVFormItem>
            </Col>
        </RVForm>
    );
}
